<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
    >
      <v-card
        class="d-flex flex-column"
      >
        <v-app-bar
          color="cyan"
          dark
          flat
        >
          <v-toolbar-title class="text-h5">
            <v-icon class="mr-2">
              link
            </v-icon>
            Associate Party
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="dialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-app-bar>

        <v-tabs
          v-model="partiesGroupFilter"
          background-color="cyan"
          centered
          dark
        >
          <v-tab
            v-if="!currentUser.isExternal"
          >
            All unassigned Parties
          </v-tab>

          <v-tab>
            Record Parties
          </v-tab>
        </v-tabs>

        <v-card-text
          class="pa-0"
        >
          <div
            class="d-flex flex-column"
          >
            <div
              class="teal lighten-4"
            >
              <v-text-field
                v-model="searchTerm"
                class="my-3 mx-2 px-2"
                clear-icon="mdi-close-circle"
                clearable
                hide-details
                label="Search Parties"
                x-placeholder="Text to Search"
                x-persistent-placeholder
                prepend-icon="mdi-magnify"
              />
            </div>

            <v-data-table
              v-model="selected"
              class="search-results"
              fixed-header
              :headers="headers"
              height="50vh"
              hide-default-footer
              :items="results"
              :items-per-page="resultsPerPage"
              :loading="isLoading"
              loading-text="Fetching results..."
              :page="currentPage"
              :server-items-length="totalResultCount"
              show-select
              @update:options="onDataTableOptionsUpdated"
              @click:clear="clearSearch"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-data-footer
            class="search-pagination"
            :items-per-page-options="availablePageSizes"
            :options="footerOptions"
            :pagination="footerPagination"
            @update:options="onPaginationOptionsUpdated"
          />

          <v-spacer />

          <v-btn
            color="secondary"
            text
            @click="dialog = false"
          >
            cancel
          </v-btn>

          <v-btn
            :disabled="associateSelectedPartiesDisabled"
            color="primary"
            @click="confirmationDialog = !confirmationDialog"
          >
            Associate Party
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmationDialog"
      max-width="290"
    >
      <v-card>
        <v-card-title class="text-h5">
          Associate Party
        </v-card-title>

        <v-card-text>
          Are you sure?
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="secondary darken-1"
            text
            @click="confirmationDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary darken-1"
            text
            @click="_ => onConfirmAction()"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'

import { alertHandler } from '@/alertHandler'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { RECORD_NAMESPACE } from '@/store/record'

export default {
  name: 'AssociateParty',

  props: {
    // `callback` gives the opportunity to run custom code after user has validated the list of parties to associate.
    // Note: If `callback` is set, the default behaviour on confirmation is skipped.
    callback: {
      default: null,
      type: Function,
    },

    // `isCreate` allows to differenciate whether we are creating a case in a new record (isCreate = true) or if we are
    // creating a case in an existing record (isCreate = false).
    isCreate: {
      default: false,
      type: Boolean,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      availablePageSizes: [
        20,
        50,
        100,
      ],

      confirmationDialog: false,
      currentPage: 1,
      isLoading: true,
      isSearching: false,
      partiesGroupFilter: 0,
      resultsPerPage: 20,
      searchTerm: '',
      selected: [],
    }
  },

  computed: {
    ...mapGetters([
      'currentUser',
    ]),

    associateSelectedPartiesDisabled () {
      return this.selected.length === 0
    },

    dialog: {
      get () { return this.showAssociatePartyDialog },

      set (val) {
        this.setShowAssociatePartyDialog(val)
      },
    },

    footerOptions () {
      return {
        itemsPerPage: this.resultsPerPage,
        page: this.currentPage,
      }
    },

    footerPagination () {
      return {
        itemsLength: this.totalResultCount,
        itemsPerPage: this.resultsPerPage,
        page: this.currentPage,
        pageCount: this.pageCount,
        pageStart: (this.currentPage - 1) * this.legalCaseUnassociatedParties.per_page,
        pageStop: this.currentPage * this.legalCaseUnassociatedParties.per_page,
      }
    },

    headers () {
      return [
        {
          align: 'start',
          text: 'First name',
          value: 'first_name',
        },
        {
          text: 'Middle name',
          value: 'middle_name',
        },
        {
          text: 'Last name',
          value: 'last_name',
        },
        {
          text: 'DOB',
          value: 'dob',
        },
        {
          text: 'Gender',
          value: 'gender',
        },
      ]
    },

    legalCaseId () {
      return this.legalCase?.id
    },

    pageCount () {
      return Math.ceil(this.totalResultCount / this.resultsPerPage)
    },

    results () {
      return this.isSearching ? [] : this.legalCaseUnassociatedParties.data
    },

    totalResultCount () {
      return this.legalCaseUnassociatedParties.total_count
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCase',
      'legalCaseUnassociatedParties',
      'paginationOptions',
      'showAssociatePartyDialog',
    ]),

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
    ]),
  },

  watch: {
    legalCaseId (newVal, oldVal) {
      if (newVal && !oldVal) this.initialize()
    },

    partiesGroupFilter () {
      this.isSearching = true
      this.fetchLegalCaseUnassociatedParties()
    },

    searchTerm () {
      this.isLoading = true
      this.isSearching = true
      this.debounceSearch()
    },

    showAssociatePartyDialog (newVal, oldVal) {
      if (newVal !== oldVal && newVal) this.initialize()
    },
  },

  methods: {
    clearSearch () {
      this.searchTerm = ''
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1
      this.fetchLegalCaseUnassociatedParties()
    }, 500),

    fetchLegalCaseUnassociatedParties () {
      this.isLoading = true

      const opts = {
        id: this.legalCaseId,
        params: {
          filter_by_record: !!this.partiesGroupFilter,
          page_number: this.currentPage,
          per_page: this.resultsPerPage,
          search_term: this.searchTerm,
          sort_by: this.paginationOptions.sortBy[0],
          sort_desc: this.paginationOptions.sortDesc[0],
        },
      }

      if (opts.params.sort_by === 'gender') opts.params.sort_by = 'gender_id'

      this.getUnassociatedParties(opts).
        finally(_ => {
          this.isLoading = false
          this.isSearching = false
        })
    },

    getUnassociatedParties (opts) {
      if (this.isCreate) {
        return this.getRecordUnassociatedParties({
          ...opts.params,
          id: this.currentRecord.id,
        })
      } else return this.getLegalCaseUnassociatedParties(opts)
    },

    initialize () {
      this.searchTerm = ''
      this.selected = []
      this.currentPage = 1
      this.partiesGroupFilter = 1
      this.isSearching = true
      this.fetchLegalCaseUnassociatedParties()
    },

    onAssociateParties () {
      const partyIds = this.selected.map(party => party.id)

      this.associateParties({ id: this.legalCaseId, partyIds }).
        then(_ => {
          this.confirmationDialog = false
          this.dialog = false
          this.searchTerm = ''
          this.selected = []
        }).
        catch(({ data }) => {
          if (data.errors) this.displayAlert('error', data.errors)
          else this.displayAlert('error', 'Internal Server Error')
        })
    },

    onConfirmAction () {
      if (this.callback) {
        this.confirmationDialog = false
        this.dialog = false
        return this.callback(this.selected)
      }

      this.onAssociateParties()
    },

    onDataTableOptionsUpdated (opts) {
      this.setPaginationOptions(opts)
      this.fetchLegalCaseUnassociatedParties()
    },

    onPaginationOptionsUpdated (options) {
      this.resultsPerPage = options.itemsPerPage
      this.currentPage = options.page
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'associateParties',
      'getLegalCaseUnassociatedParties',
      'setPaginationOptions',
      'setShowAssociatePartyDialog',
    ]),

    ...mapActions(RECORD_NAMESPACE, [
      'getRecordUnassociatedParties',
    ]),
  },
}
</script>

<style scoped>
.search-results {
  .v-data-table__wrapper {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.search-pagination {
  .v-data-footer__select {
    margin-left: 0;
  }
}
</style>
